import MUIDataTable from "mui-datatables";
import * as React from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import api from '../services/api'
import './style.css'
import { Alert, Badge, Box, Container, IconButton, Snackbar, ThemeProvider, Tooltip, CircularProgress } from "@mui/material";
import theme from "../themeDash";
import { grey, lightBlue, red, teal } from "@mui/material/colors";
import { yellow } from "@mui/material/colors";
import { blueGrey } from "@mui/material/colors";
import { orange } from "@mui/material/colors";
import { lime } from "@mui/material/colors";
import { green } from "@mui/material/colors";
import moment from "moment";
import 'moment/locale/pt-br'
import { Button, Typography } from "@mui/material";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import PhoneIcon from '@mui/icons-material/Phone';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useAuth } from '../auth/AuthProvider'
import { useNavigate } from "react-router-dom";
import FormDialog from "./FormDialog";
import FormDialogEmpresa from '../Empresa/FormDialog'
import FormDialogPPI from './FormDialogPPI'
import NotifyOptionsProvider from './Notify'
import PerfOverlay from '../components/PerfOverlay'
import ApiDownBanner from './ApiDownBanner'

// Phase 3a: Cache de moment() para evitar recalculos
const socFormatClockCache = new Map()
function formatClock(clock) {
  if (socFormatClockCache.has(clock)) return socFormatClockCache.get(clock)
  const result = moment(clock).locale('pt-br').format('DD/MM HH:mm')
  socFormatClockCache.set(clock, result)
  return result
}

let socFromNowEpoch = 0
const socFromNowCache = new Map()
function fromNowClock(clock, epoch) {
  if (epoch !== socFromNowEpoch) {
    socFromNowCache.clear()
    socFromNowEpoch = epoch
  }
  if (socFromNowCache.has(clock)) return socFromNowCache.get(clock)
  const result = moment(clock).locale('pt-br').fromNow()
  socFromNowCache.set(clock, result)
  return result
}

const MAX_ANIMATION_IDS = 20;


export default function SOC() {
  const [problems, setProblems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [totalCount, setTotalCount] = React.useState(0)
  const [page, setPage] = React.useState(0)

  const [newAlarmIds, setNewAlarmIds] = React.useState(new Set())
  const [resolvingIds, setResolvingIds] = React.useState(new Set())
  const [dismissSnack, setDismissSnack] = React.useState({ open: false, message: '', severity: 'success' })

  // Phase 3a: epoch counter para invalidar cache de fromNow
  const [timeEpoch, setTimeEpoch] = React.useState(0)

  // Phase 7: rastrear ultimo evento WS
  const lastWsUpdate = React.useRef(0)

  // Phase 5: timer ref unico para animacoes
  const animationTimerRef = React.useRef(null)

  // PerfOverlay: render counter + show flag
  const renderCountRef = React.useRef(0)
  renderCountRef.current++
  const showPerf = process.env.NODE_ENV === 'development' || new URLSearchParams(window.location.search).has('perf')

  // Refs para desacoplar columns/options de state volatil (evita re-render do MUIDataTable)
  const problemsRef = React.useRef(problems)
  React.useEffect(() => { problemsRef.current = problems }, [problems])

  const newAlarmIdsRef = React.useRef(newAlarmIds)
  React.useEffect(() => { newAlarmIdsRef.current = newAlarmIds }, [newAlarmIds])

  const resolvingIdsRef = React.useRef(resolvingIds)
  React.useEffect(() => { resolvingIdsRef.current = resolvingIds }, [resolvingIds])

  const handleDismiss = (cod_empresa, event_id) => {
    if (!window.confirm('Dispensar este alarme do painel ativo?')) return
    api.post('/webhooks/dismiss', { cod_empresa, event_id })
      .then(() => {
        setProblems(prev => prev.filter(p => !(p.cod_empresa === cod_empresa && p.event_id === event_id)))
        setTotalCount(prev => Math.max(0, prev - 1))
        setDismissSnack({ open: true, message: 'Alarme dispensado', severity: 'success' })
      })
      .catch((err) => {
        const msg = err.response?.status === 422
          ? err.response.data.detail
          : 'Erro ao dispensar alarme'
        setDismissSnack({ open: true, message: msg, severity: 'warning' })
      })
  }
  const [rowsPerPage, setRowsPerPage] = React.useState(() => {
    const saved = localStorage.getItem('socPage')
    return saved ? JSON.parse(saved) : 100
  })

  const initialFilters = React.useRef(null)
  if (initialFilters.current === null) {
    try {
      const saved = localStorage.getItem('socFilters')
      const parsed = saved ? JSON.parse(saved) : {}
      initialFilters.current = {
        search: parsed.search || '',
        cod_empresa: (parsed.cod_empresa || []).map(Number),
        severity: (parsed.severity || []).map(Number),
        acks: (parsed.acks || []).map(Number),
      }
    } catch {
      initialFilters.current = { search: '', cod_empresa: [], severity: [], acks: [] }
    }
  }

  const [searchText, setSearchText] = React.useState(initialFilters.current.search)
  const [filterEmpresa, setFilterEmpresa] = React.useState(initialFilters.current.cod_empresa)
  const [filterSeverity, setFilterSeverity] = React.useState(initialFilters.current.severity)
  const [filterAcks, setFilterAcks] = React.useState(initialFilters.current.acks)
  const [sortColumn, setSortColumn] = React.useState('clock')
  const [sortDirection, setSortDirection] = React.useState('desc')
  const searchTimeoutRef = React.useRef(null)

  const isFirstRender = React.useRef(true)
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    localStorage.setItem('socFilters', JSON.stringify({
      search: searchText,
      cod_empresa: filterEmpresa,
      severity: filterSeverity,
      acks: filterAcks,
    }))
  }, [searchText, filterEmpresa, filterSeverity, filterAcks])

  // Phase 6: filtersRef para estabilizar event listeners
  const filtersRef = React.useRef({
    page: 0, rowsPerPage: 100, filterEmpresa: [], filterSeverity: [],
    filterAcks: [], searchText: '', sortColumn: 'clock', sortDirection: 'desc',
  })
  React.useEffect(() => {
    filtersRef.current = {
      page, rowsPerPage, filterEmpresa, filterSeverity, filterAcks,
      searchText, sortColumn, sortDirection,
    }
  }, [page, rowsPerPage, filterEmpresa, filterSeverity, filterAcks, searchText, sortColumn, sortDirection])

  const [handlerDialog, setHandlerDialog] = React.useState(false)

  const [handlerDialogEmpresa, setHandlerDialogEmpresa] = React.useState(false)
  const [handlerDialogPPI, sethandlerDialogPPI] = React.useState(false)
  const [severity, setSeverity] = React.useState(false)
  const [hostid, setHostid] = React.useState('')
  const [codEmpresa, setCodEmpresa] = React.useState('')
  const [tags, setTags] = React.useState('')
  const [ppi, setPpi] = React.useState('')
  const [eventId, setEventId] = React.useState('')
  const [update, setUpdate] = React.useState('')
  const [detailAlarms, setDetailAlarms] = React.useState('')
  const [bulkAlarms, setBulkAlarms] = React.useState([])
  const selectedAlarmIds = React.useRef(new Set())

  const [id, setId] = React.useState()
  const auth = useAuth()
  const [empresas, setEmpresas] = React.useState([])
  const navigate = useNavigate()

  const getEmpresas = () => api.get('/empresa/all').then(res => setEmpresas(res.data.filter(empresa => empresa.status == 1).map(empresa => empresa.cod_empresa)))

  const openAckDialog = (problem) => {
    setId(problem.event_id)
    setHostid(problem.hostid || false)
    setTags(problem.tags)
    setCodEmpresa(problem.cod_empresa)
    setEventId(problem.event_id)
    setSeverity(problem.severity)
    setDetailAlarms(`${problem.hostname || ''} - ${problem.name || ''}`)
    setHandlerDialog(true)
  }

  const getProblems = React.useCallback((
    currentPage = page,
    currentRowsPerPage = rowsPerPage,
    search = searchText,
    empresaFilter = filterEmpresa,
    severityFilter = filterSeverity,
    acksFilter = filterAcks,
    sortCol = sortColumn,
    sortDir = sortDirection,
  ) => {
    setLoading(true)

    const params = new URLSearchParams()
    params.append('page', currentPage + 1)
    params.append('per_page', currentRowsPerPage)
    params.append('source', 'wazuh')

    if (search) params.append('search', search)
    if (empresaFilter.length > 0) params.append('cod_empresa', empresaFilter.join(','))
    if (severityFilter.length > 0) params.append('severity', severityFilter.join(','))
    if (acksFilter.length > 0) params.append('acks', acksFilter.join(','))
    if (sortCol) params.append('sort_column', sortCol)
    if (sortDir) params.append('sort_direction', sortDir)

    api.get(`/dashboard/problems/?${params.toString()}`)
      .then(res => {
        if (res.data.items) {
          setProblems(res.data.items)
          setTotalCount(res.data.total)
        } else {
          setProblems(res.data)
          setTotalCount(res.data.length)
        }
      })
      .catch((err) => {
        if (err.response?.status == 401) {
          navigate('/401')
        }
        setProblems([])
      })
      .finally(() => setLoading(false))
  }, [page, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection, navigate])

  const columns = React.useMemo(() => [
    {
      name: "event_id",
      label: "Evento ID",
      options: {
        filter: false,
        sort: false,
        display: false,
      }
    },
    {
      name: "cod_empresa",
      label: "Alarme",
      options: {
        filter: true,
        sort: true,
        display: true,
        filterList: filterEmpresa,
        customFilterListOptions: { render: v => `Empresa: ${v}` },
        filterOptions: {
          names: [...new Set([...filterEmpresa, ...empresas])].sort((a, b) => a - b),
        },
        customBodyRenderLite: (dataIndex) => {
          const problem = problemsRef.current[dataIndex]
          if (!problem) return null
          return (
            <Box sx={{ py: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography fontSize={14} fontWeight="bold">{problem.cod_empresa}</Typography>
                  <Typography fontSize={14}>-</Typography>
                  <Typography fontSize={14} fontWeight="bold">{problem.hostname}</Typography>
                  {problem.ip && <Typography fontSize={14} sx={{ opacity: 0.85 }}>({problem.ip})</Typography>}
                  {problem.occurrence_count > 1 && <Badge badgeContent={problem.occurrence_count} color="warning" max={9999} sx={{ ml: 2, mr: 1 }} />}
                </Box>
                <Typography fontSize={14} noWrap>
                  {formatClock(problem.clock)} ({fromNowClock(problem.clock, timeEpoch)})
                </Typography>
              </Box>
              <Typography fontSize={14}>{problem.name}</Typography>
            </Box>
          )
        }
      }
    },
    {
      name: "name",
      label: "Descricao",
      options: {
        filter: false,
        sort: false,
        display: false,
      }
    },
    {
      name: "hostname",
      label: "Agente",
      options: {
        filter: false,
        sort: false,
        display: false,
      }
    },
    {
      name: "ip",
      label: "IP",
      options: {
        filter: false,
        sort: false,
        display: false,
      }
    },
    {
      name: "clock",
      label: "Quando",
      options: {
        filter: false,
        sort: true,
        sortOrder: 'desc',
        display: false,
      }
    },
    {
      name: "severity",
      label: "Severidade",
      options: {
        filter: true,
        sort: false,
        display: false,
        filterList: filterSeverity,
        customFilterListOptions: {
          render: v => {
            const labels = { 1: 'Info', 2: 'Aviso', 3: 'Media', 4: 'Alta', 5: 'Desastre' }
            return `Sev: ${labels[v] || v}`
          }
        },
        filterOptions: {
          names: [1, 2, 3, 4, 5],
          renderValue: v => {
            const labels = { 1: '1 - Info', 2: '2 - Aviso', 3: '3 - Media', 4: '4 - Alta', 5: '5 - Desastre' }
            return labels[v] || v
          }
        },
      }
    },
    {
      name: "occurrence_count",
      label: "Ocorrencias",
      options: {
        filter: false,
        sort: true,
        display: false,
      }
    },
    {
      name: "acks",
      label: "Vistos",
      options: {
        filter: true,
        sort: true,
        display: false,
        filterList: filterAcks,
        customFilterListOptions: { render: v => v == 0 ? 'Nao vistados' : 'Vistados' },
        filterOptions: {
          names: [0, 1],
          renderValue: v => v == 0 ? 'Nao vistados' : 'Vistados'
        }
      }
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const problem = problemsRef.current[dataIndex]
          if (!problem) return null

          const acks = problem.acks

          return (
            <Box sx={{ display: 'flex' }}>
              {acks > 0 ?
                <Tooltip title={problem.tags || "Reconhecer alarme"} >
                  <IconButton disabled={!auth.hasPerm('problem.edit')} sx={{
                    backgroundColor: grey[900],
                    color: "white"
                  }}
                    onClick={() => {
                      setId(problem.event_id)
                      setHostid(false)
                      setTags(problem.tags)
                      setCodEmpresa(problem.cod_empresa)
                      setEventId(problem.event_id)
                      setSeverity(problem.severity)
                      setDetailAlarms(`${problem.hostname} - ${problem.name}`)
                      setHandlerDialog(true)
                    }}
                  >
                    <Badge badgeContent={acks} color="success" >
                      <AssignmentTurnedInIcon sx={{ color: "white" }} />
                    </Badge>
                  </IconButton>
                </Tooltip>
                :
                <Tooltip title={problem.tags || "Reconhecer alarme"} >
                  <IconButton disabled={!auth.hasPerm('problem.edit')} sx={{ color: grey[900] }}
                    onClick={() => {
                      setTags(problem.tags)
                      setId(problem.event_id)
                      setCodEmpresa(problem.cod_empresa)
                      setDetailAlarms(`${problem.hostname} - ${problem.name}`)
                      setEventId(problem.event_id)
                      setSeverity(problem.severity)
                      setHandlerDialog(true)
                    }}
                  >
                    <AssignmentLateIcon sx={{ color: grey[900] }} />
                  </IconButton>
                </Tooltip>
              }
              <Tooltip title="Informacoes da Empresa">
                <IconButton sx={{ color: grey[900] }}
                  onClick={() => {
                    const cod_empresa = problem.cod_empresa
                    api.get(`/empresa/find/cod_empresa/${cod_empresa}`).then(res => {
                      setId(res.data.id)
                      setCodEmpresa(cod_empresa)
                      setHandlerDialogEmpresa(true)
                    }).catch(err => {
                      const msg = err?.response?.status === 404
                        ? 'Empresa não cadastrada no Horus'
                        : 'Erro ao buscar empresa'
                      setDismissSnack({ open: true, message: msg, severity: 'error' })
                    })
                  }}
                >
                  <LiveHelpIcon />
                </IconButton>
              </Tooltip>
              {problem.tags_ppi > 0 ?
                <Tooltip title="Procedimento do alarme">
                  <IconButton sx={{
                    backgroundColor: grey[900],
                    color: "white"
                  }}
                    onClick={() => {
                      setPpi(problem.tags_ppi)
                      sethandlerDialogPPI(true)
                    }}
                  >
                    <ArticleIcon />
                  </IconButton>
                </Tooltip>
                : ""}
              <Tooltip title="Dispensar alarme">
                <IconButton disabled={!auth.hasPerm('problem.edit')} sx={{ color: grey[900] }}
                  onClick={() => handleDismiss(problem.cod_empresa, problem.event_id)}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
        sort: false,
        display: true,
      }
    },
    {
      name: "tags_ppi",
      label: "PPI",
      options: {
        filter: false,
        sort: false,
        display: false,
      }
    },
  ], [filterEmpresa, filterSeverity, filterAcks, empresas, timeEpoch, auth])

  const rowsSelected = React.useMemo(() => {
    if (selectedAlarmIds.current.size === 0) return []
    return problems
      .map((p, idx) => selectedAlarmIds.current.has(`${p.cod_empresa}:${p.event_id}`) ? idx : -1)
      .filter(idx => idx !== -1)
  }, [problems])

  const options = React.useMemo(() => ({
    serverSide: true,
    count: totalCount,
    page: page,
    rowsPerPage: rowsPerPage,
    searchText: searchText,
    customToolbar: () => {
      return (<>
        <Tooltip title="Remover todos os filtros">
          <IconButton onClick={() => {
            setSearchText('')
            setFilterEmpresa([])
            setFilterSeverity([])
            setFilterAcks([])
            setSortColumn('clock')
            setSortDirection('desc')
            setPage(0)
            localStorage.removeItem('socFilters')
            getProblems(0, rowsPerPage, '', [], [], [], 'clock', 'desc')
          }} ><RemoveCircleOutlineIcon /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
      </>
      )
    },
    onChangePage: (newPage) => {
      setPage(newPage)
      setNewAlarmIds(new Set())
      setResolvingIds(new Set())
      getProblems(newPage, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection)
    },
    onChangeRowsPerPage: (newRowsPerPage) => {
      setRowsPerPage(newRowsPerPage)
      setPage(0)
      localStorage.setItem('socPage', JSON.stringify(newRowsPerPage))
      getProblems(0, newRowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection)
    },
    onSearchChange: (searchQuery) => {
      setSearchText(searchQuery || '')
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      searchTimeoutRef.current = setTimeout(() => {
        setPage(0)
        getProblems(0, rowsPerPage, searchQuery || '', filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection)
      }, 300)
    },
    onFilterChange: (changedColumn, filterList, type) => {
      if (type === 'reset') return

      const newFilterEmpresa = filterList[1] || []
      const newFilterSeverity = filterList[6] || []
      const newFilterAcks = filterList[8] || []

      setFilterEmpresa(newFilterEmpresa)
      setFilterSeverity(newFilterSeverity)
      setFilterAcks(newFilterAcks)
      setPage(0)
      getProblems(0, rowsPerPage, searchText, newFilterEmpresa, newFilterSeverity, newFilterAcks, sortColumn, sortDirection)
    },
    onColumnSortChange: (changedColumn, direction) => {
      const newSortDir = direction === 'asc' ? 'asc' : 'desc'
      setSortColumn(changedColumn)
      setSortDirection(newSortDir)
      setPage(0)
      getProblems(0, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, changedColumn, newSortDir)
    },
    customToolbarSelect: (selectedRows) =>
      <Tooltip title="Reconhecimento em massa">
        <IconButton
          disabled={!auth.hasPerm('problem.edit')}
          onClick={() => {
            const probs = problemsRef.current
            const eventsIds = selectedRows.data.map(row => probs[row.dataIndex].event_id)
            const details = selectedRows.data.map(row => `${probs[row.dataIndex].hostname} - ${probs[row.dataIndex].name}`)
            const empresas = selectedRows.data.map(row => probs[row.dataIndex].cod_empresa)

            if (!empresas.every(v => v == empresas[0])) {
              alert("Selecione apenas alarmes referentes a mesma empresa!")
              return;
            }
            const alarms = selectedRows.data.map(row => {
              const p = probs[row.dataIndex]
              return { hostname: p.hostname, ip: p.ip, name: p.name, severity: p.severity, clock: p.clock, occurrences: p.occurrences || 1, cod_empresa: p.cod_empresa }
            })
            setEventId(eventsIds)
            setDetailAlarms(details)
            setCodEmpresa(empresas[0])
            setBulkAlarms(alarms)
            setHandlerDialog(true)
          }}><AssignmentLateIcon /></IconButton>
      </Tooltip>,
    filterType: 'checkbox',
    rowsPerPageOptions: [10, 30, 50, 100, 200],
    rowHover: false,
    print: false,
    download: true,
    searchAlwaysOpen: true,
    setRowProps: (row, dataIndex) => {
      const problem = problemsRef.current[dataIndex]
      const alarmId = problem ? `${problem.cod_empresa}:${problem.event_id}` : null

      const isNew = alarmId && newAlarmIdsRef.current.has(alarmId)
      const isResolving = alarmId && resolvingIdsRef.current.has(alarmId)

      let bgColor
      switch (row[6]) {
        case 5:
          bgColor = theme.palette.mode == 'dark' ? red[400] : red[300]
          break
        case 4:
          bgColor = theme.palette.mode == 'dark' ? orange[300] : orange[200]
          break
        case 3:
          bgColor = theme.palette.mode == 'dark' ? lime[300] : lime[200]
          break
        case 2:
          bgColor = theme.palette.mode == 'dark' ? lightBlue[300] : lightBlue[200]
          break
        case 1:
          bgColor = theme.palette.mode == 'dark' ? blueGrey[300] : blueGrey[200]
          break
        default:
          bgColor = undefined
      }

      const style = {
        backgroundColor: bgColor,
        transition: 'all 0.3s ease-in-out',
      }

      if (isNew) {
        style.animation = 'fadeIn 0.5s ease-out'
        style.borderLeft = '4px solid #ff9800'
      }

      if (isResolving) {
        style.animation = 'fadeOut 2s ease-out forwards'
        style.backgroundColor = green[400]
      }

      return { style }
    },
    rowsSelected: rowsSelected,
    onTableChange: (action, tableState) => {
      if (action === 'rowSelectionChange') {
        const ids = new Set(
          tableState.selectedRows.data.map(row => {
            const p = problemsRef.current[row.dataIndex]
            return p ? `${p.cod_empresa}:${p.event_id}` : null
          }).filter(Boolean)
        )
        selectedAlarmIds.current = ids
      }
    },
  }), [totalCount, page, rowsPerPage, searchText, rowsSelected, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection, loading, auth, getProblems])

  const refreshData = React.useCallback(() => {
    if (selectedAlarmIds.current.size > 0) return
    getProblems(page, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection)
  }, [page, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection, getProblems])

  // Auto-refresh a cada 60s + incrementa timeEpoch
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeEpoch(e => e + 1)
      const elapsed = Date.now() - lastWsUpdate.current
      if (elapsed < 60000) return
      refreshData()
    }, 60 * 1000)
    return () => clearInterval(interval)
  }, [refreshData])

  // Escuta evento do WebSocket para refresh imediato
  React.useEffect(() => {
    const handleRefresh = () => {
      if (selectedAlarmIds.current.size === 0) {
        refreshData()
      }
    }
    window.addEventListener('alarms-refresh', handleRefresh)
    return () => window.removeEventListener('alarms-refresh', handleRefresh)
  }, [refreshData])

  // Escuta novos alarmes do WebSocket (filtra apenas wazuh)
  // Phase 6: listener registrado uma vez, le filtros de filtersRef
  React.useEffect(() => {
    const handleNewAlarms = (event) => {
      if (selectedAlarmIds.current.size > 0) return
      const f = filtersRef.current
      if (f.page !== 0) return

      const newAlarms = event.detail
      if (!newAlarms || !Array.isArray(newAlarms) || newAlarms.length === 0) return

      lastWsUpdate.current = Date.now()

      const matchesFilters = (alarm) => {
        // API Down (sev=6) nao entra no SOC — fica no banner ApiDownBanner
        if (Number(alarm.severity) === 6) return false
        if ((alarm.source || 'zabbix') !== 'wazuh') return false
        if (f.filterEmpresa.length > 0 && !f.filterEmpresa.includes(alarm.cod_empresa)) return false
        if (f.filterSeverity.length > 0 && !f.filterSeverity.includes(alarm.severity)) return false
        if (f.filterAcks.length > 0) {
          const hasAck = alarm.acks > 0
          const wantNoAck = f.filterAcks.includes(0)
          const wantAck = f.filterAcks.some(a => a > 0)
          if (wantNoAck && hasAck) return false
          if (wantAck && !hasAck) return false
        }
        if (f.searchText) {
          const search = f.searchText.toLowerCase()
          const name = String(alarm.name || '').toLowerCase()
          const hostname = String(alarm.hostname || '').toLowerCase()
          const ip = String(alarm.ip || '').toLowerCase()
          const cod = String(alarm.cod_empresa || '').toLowerCase()
          if (!name.includes(search) && !hostname.includes(search) && !ip.includes(search) && !cod.includes(search)) {
            return false
          }
        }
        return true
      }

      const filteredNew = newAlarms.filter(matchesFilters)
      if (filteredNew.length === 0) return

      const newIds = filteredNew.map(a => `${a.cod_empresa}:${a.event_id}`)

      // Phase 2: batch all state updates
      unstable_batchedUpdates(() => {
        // Phase 5: cap animacao
        setNewAlarmIds(prev => {
          const merged = new Set([...prev, ...newIds])
          if (merged.size > MAX_ANIMATION_IDS) return new Set(newIds.slice(0, MAX_ANIMATION_IDS))
          return merged
        })

        setProblems(prev => {
          const existingHashes = new Set(prev.map(p => `${p.cod_empresa}:${p.event_id}`))
          const uniqueNew = filteredNew.filter(a => !existingHashes.has(`${a.cod_empresa}:${a.event_id}`))
          if (uniqueNew.length === 0) return prev

          let merged = [...uniqueNew, ...prev]
          const clockSortFn = f.sortDirection === 'desc'
            ? (a, b) => new Date(b.clock) - new Date(a.clock)
            : (a, b) => new Date(a.clock) - new Date(b.clock)
          merged.sort(clockSortFn)

          // Phase 8: conta apenas alarmes dedupados
          setTotalCount(pc => pc + uniqueNew.length)

          return merged.slice(0, f.rowsPerPage)
        })
      })

      // Phase 5: timer unico para limpeza
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current)
      animationTimerRef.current = setTimeout(() => {
        setNewAlarmIds(prev => {
          const updated = new Set(prev)
          newIds.forEach(id => updated.delete(id))
          return updated
        })
      }, 10000)
    }

    window.addEventListener('alarms-new', handleNewAlarms)
    return () => window.removeEventListener('alarms-new', handleNewAlarms)
  }, [])

  // Escuta alarmes removidos do WebSocket
  React.useEffect(() => {
    const handleRemovedAlarms = (event) => {
      const removed = event.detail
      if (!removed || !Array.isArray(removed) || removed.length === 0) return

      lastWsUpdate.current = Date.now()

      const removedIds = removed.map(a => `${a.cod_empresa}:${a.event_id}`)
      setResolvingIds(new Set(removedIds))

      setTimeout(() => {
        unstable_batchedUpdates(() => {
          setProblems(prev => {
            const removedSet = new Set(removedIds)
            return prev.filter(p => !removedSet.has(`${p.cod_empresa}:${p.event_id}`))
          })
          setResolvingIds(new Set())
          setTotalCount(prev => Math.max(0, prev - removed.length))
        })
      }, 2000)
    }

    window.addEventListener('alarms-removed', handleRemovedAlarms)
    return () => window.removeEventListener('alarms-removed', handleRemovedAlarms)
  }, [])

  // Escuta ack_update do WebSocket
  React.useEffect(() => {
    const handleAckUpdate = (event) => {
      const { cod_empresa, event_ids } = event.detail
      if (!cod_empresa || !event_ids) return

      lastWsUpdate.current = Date.now()

      const eidSet = new Set(event_ids.map(Number))
      unstable_batchedUpdates(() => {
        setProblems(prev => prev.map(p => {
          if (Number(p.cod_empresa) === Number(cod_empresa) && eidSet.has(Number(p.event_id))) {
            return { ...p, acks: (p.acks || 0) + 1 }
          }
          return p
        }))
      })
    }

    window.addEventListener('ack-update', handleAckUpdate)
    return () => window.removeEventListener('ack-update', handleAckUpdate)
  }, [])

  // Titulo dinamico
  React.useEffect(() => {
    const baseTitle = 'Horus'
    if (totalCount > 0) {
      document.title = `(${totalCount}) SOC - ${baseTitle}`
    } else {
      document.title = `SOC - ${baseTitle}`
    }
    return () => { document.title = baseTitle }
  }, [totalCount])

  // Carrega inicial
  React.useEffect(() => {
    getProblems(0, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection)
    getEmpresas()
  }, [])

  // Refresh apos ack
  React.useEffect(() => {
    if (update) {
      getProblems(page, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection)
      setUpdate(false)
    }
  }, [update, page, rowsPerPage, searchText, filterEmpresa, filterSeverity, filterAcks, sortColumn, sortDirection, getProblems])

  return (
    <NotifyOptionsProvider problems={{ problems }}>
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, height: 'auto' }}>
        <FormDialog open={handlerDialog} codEmpresa={codEmpresa} tags={tags} eventId={eventId} hostid={hostid} severity={severity} detailAlarms={detailAlarms} source="wazuh" bulkAlarms={bulkAlarms} handlerClose={() => {
          setHandlerDialog(false)
          setUpdate(true)
        }} />
        <FormDialogEmpresa open={handlerDialogEmpresa} codEmpresa={codEmpresa} id={id} handlerClose={() => setHandlerDialogEmpresa(false)} />
        <FormDialogPPI open={handlerDialogPPI} id={ppi} handlerClose={() => {
          sethandlerDialogPPI(false)
        }} />
        <ThemeProvider theme={theme}>
          <ApiDownBanner source="wazuh" onAck={openAckDialog} />
          <Box sm={12} lg={12}>
            <MUIDataTable
              title={"SOC - Alertas de Seguranca"}
              data={problems}
              columns={columns}
              options={options}
            />
          </Box>
        </ThemeProvider>
      </Container>
      <Snackbar open={dismissSnack.open} autoHideDuration={3000}
        onClose={() => setDismissSnack({ ...dismissSnack, open: false })}>
        <Alert severity={dismissSnack.severity} onClose={() => setDismissSnack({ ...dismissSnack, open: false })}>
          {dismissSnack.message}
        </Alert>
      </Snackbar>
      {showPerf && <PerfOverlay renderCount={renderCountRef.current} />}
    </NotifyOptionsProvider>
  )
}
